import { Empty, IconFont } from '@byecode/ui'
import { BlockType } from '@lighthouse/core'
import { useAtomAction, useAtomData } from '@lighthouse/shared'
import { animated, useSpring } from '@react-spring/web'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router'
import styled from 'styled-components'

import { dataDrawerStateAtom } from '@/atoms/application/state'
import {
    lastPageOfStackAtom,
    pageVariableRecordMapAtom,
    pageVariableSchemasFamily,
    selectedBlockContextAtom,
    selectedBlockFormContainerAtom
} from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource } from '@/hooks/useDataSource'

import { ChartDataSource } from './ChartDataSource'
import { ViewDataSource } from './ViewDataSource'

const SCxContainer = styled(animated.div)`
    position: absolute;
    bottom: 12px;
    left: 16px;
    right: 16px;
    z-index: 200;

    height: 355px;
    background: var(--color-white);
    border: 1px solid var(--color-gray-200);

    border-radius: 17px;
    box-shadow: 0px 4px 12px 0px #1018281a;
`

const SCxWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 8px;
    height: 100%;
    position: relative;
`

const SCxHeader = styled.div`
    position: absolute;
    top: -16px;
    width: 134px;
    height: 18px;
    left: 50%;
    z-index: 200;
    margin-left: -67px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`

const SCxArrowIcon = styled(IconFont)`
    position: absolute;
    top: 5px;
`

export const PageDataDrawer: React.FC = () => {
    const dataDrawerVisible = useAtomData(dataDrawerStateAtom)
    const { run: onVisibleDataDrawer } = useAtomAction(dataDrawerStateAtom)
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const containerRef = useRef<HTMLDivElement>(null)

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const stackId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.stackId || '', [])
    )

    // 直接从 blockContextIndex 获取选中的 block
    const blockData = useAtomData(
        selectedBlockContextAtom,
        useCallback(ctx => ctx?.block, [])
    )

    // 直接从 blockContextIndex 获取父级 FormContainer
    const parentFormPointer = useAtomData(
        selectedBlockFormContainerAtom,
        useCallback(form => form?.config.pointer, [])
    )

    const [springs, api] = useSpring(() => ({
        from: { y: 355 },
        to: { y: 0 },
        config: {
            duration: 300
        }
    }))

    const handleHidden = useCallback(async () => {
        const isEnd = api.start({
            from: {
                y: 0
            },
            to: {
                y: 379
            },
            config: {
                duration: 300
            }
        })
        const resultList = await Promise.all(isEnd)
        const result = resultList.every(item => item.finished)
        if (result) {
            onVisibleDataDrawer?.(false)
        }
    }, [api, onVisibleDataDrawer])

    const pageVariableSchema = useAtomData(pageVariableSchemasFamily(pageId))
    const pageRecordMap = useAtomData(
        pageVariableRecordMapAtom,
        useCallback(s => s[stackId] ?? {}, [stackId])
    )
    const pageRecordDependInfo = useMemo(() => {
        const firstVariableSchema = pageVariableSchema?.[0]
        if (!firstVariableSchema) {
            return
        }
        return pageRecordMap[firstVariableSchema.id]
    }, [pageRecordMap, pageVariableSchema])

    const dsId = useMemo(() => {
        if (!blockData) {
            return pageRecordDependInfo?.dsId
        }
        if (blockData.type === BlockType.chart || blockData.type === BlockType.view || blockData.type === BlockType.formContainer) {
            const { pointer } = blockData.config
            return pointer
        }
        return parentFormPointer || pageRecordDependInfo?.dsId
    }, [blockData, pageRecordDependInfo?.dsId, parentFormPointer])

    const dataSource = useDataSource(appId, envId, dsId || '')

    const location = useLocation()

    useEffect(() => {
        if (!location.pathname.includes('/page') && dataDrawerVisible) {
            onVisibleDataDrawer?.(false)
        }
    }, [location.pathname, onVisibleDataDrawer, dataDrawerVisible])

    const content = useMemo(() => {
        if (!dataSource) {
            return (
                <Empty
                    styles={{ title: { color: 'var(--color-gray-400)!important', fontWeight: 'normal!important' } }}
                    icon={<IconFont type="SearchData" fill="var(--color-gray-400)" size={48} />}
                    title="该对象未绑定任何数据"
                />
            )
        }
        if (blockData?.type === 'view') {
            return <ViewDataSource pageId={pageId} blockData={blockData} dataSource={dataSource} />
        }
        if (blockData?.type === 'chart') {
            return <ChartDataSource pageId={pageId} blockData={blockData} dataSource={dataSource} />
        }
        return (
            <Empty
                styles={{ title: { color: 'var(--color-gray-400)!important', fontWeight: 'normal!important' } }}
                icon={<IconFont type="SearchData" fill="var(--color-gray-400)" size={48} />}
                title="该对象未绑定任何数据"
            />
        )
    }, [blockData, dataSource, pageId])

    return (
        <SCxContainer data-ignore-click-away ref={containerRef} style={{ ...springs }}>
            <SCxWrapper>
                <SCxHeader onClick={handleHidden}>
                    <svg width="136" height="20" viewBox="0 0 136 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="1" y="16" width="134.5" height="4" fill="white" />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M1.18359 16.4722C11.4814 13.1075 17.5025 10.5007 27.4175 3.66904C29.437 2.27752 31.8147 1.48215 34.267 1.44884L66.3156 1.01358V1H67.3156H101.064C102.985 1 104.879 1.45914 106.579 2.35288C109.951 4.12508 113.2 5.87795 116.259 7.52839C124.475 11.9614 131.322 15.6555 135.5 17L66.3156 16.4722H1.18359Z"
                            fill="white"
                        />
                        <path
                            d="M1 16.4722C11.2763 13.1146 17.5629 10.0996 27.4908 3.22375C29.5527 1.79569 31.9921 1 34.5002 1H102.129C104.637 1 107.077 1.79569 109.139 3.22375C119.067 10.0996 125.353 13.1146 135.629 16.4722"
                            stroke="#E4E7EC"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <SCxArrowIcon size={16} fill="var(--color-gray-400" type="ArrowDownDouble" />
                </SCxHeader>
                {content}
            </SCxWrapper>
        </SCxContainer>
    )
}
